import { useEffect, useState } from "react";
import { useBackdropDismiss } from "@/hooks/useBackdropDismiss";
import { useConfirm } from "@/hooks/useConfirm";
import { useToast } from "@/hooks/useToast";
import { Icon } from "@/components/ui/Icon";
import type { UserCalendarsApi } from "@/hooks/useUserCalendars";
import type { GoogleCalendarApi } from "@/hooks/useGoogleCalendar";
import {
  CALENDAR_COLOR_KEYS,
  CALENDAR_NAME_MAX,
  USER_CALENDARS_MAX,
  calendarColorCss,
  nextCalendarColor,
  type CalendarColorKey,
  type UserCalendar,
} from "@/domain/calendar";
import { GoogleImportModal } from "./GoogleImportModal";

/**
 * 캘린더 설정 모달 — 캘린더 목록 카드의 ⚙ 에서 연다 (2026-10-29).
 * 평면 폼 모달(.modal-backdrop > .modal-panel, 본보기 PersonalEventModal.tsx).
 *
 *   내 캘린더   기본("내 일정", 잠김) + 내가 만든 것: 이름 바꾸기(연필 → 인라인 입력) · 색(점 누르면 팔레트) · 삭제(일정은 기본으로 이동)
 *               + [캘린더 추가] 인라인 폼(이름 + 색 10개)
 *   연동 캘린더 Google — 미연결: [Google 연결](승인 뒤 가져오기 선택 창 자동) / 연결: 계정 · 가져온 목록(제거) · [가져오기 변경] [동기화] [연결 해제]
 *               네이버 — 준비 중(비활성 줄)
 */
interface Props {
  cal: UserCalendarsApi;
  g: GoogleCalendarApi;
  onClose: () => void;
}

function syncLabel(ms: number | undefined): string {
  if (!ms) return "아직 동기화 전";
  const d = new Date(ms);
  const hm = d.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit", hour12: false });
  const sameDay = d.toDateString() === new Date().toDateString();
  return `${sameDay ? "" : `${d.getMonth() + 1}/${d.getDate()} `}${hm} 동기화`;
}

function ColorPalette({ value, onPick, disabled }: { value: CalendarColorKey; onPick: (k: CalendarColorKey) => void; disabled?: boolean }) {
  return (
    <div className="my-cset-palette" role="radiogroup" aria-label="캘린더 색">
      {CALENDAR_COLOR_KEYS.map((k) => (
        <button
          key={k}
          type="button"
          role="radio"
          aria-checked={value === k}
          aria-label={`색 ${k}`}
          className={`my-cset-swatch${value === k ? " is-on" : ""}`}
          style={{ background: calendarColorCss(k) }}
          onClick={() => onPick(k)}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

export function CalendarSettingsModal({ cal, g, onClose }: Props) {
  const confirm = useConfirm();
  const toast = useToast();
  const dismiss = useBackdropDismiss(onClose);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState<CalendarColorKey>(() => nextCalendarColor(cal.own));
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [colorFor, setColorFor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [importOpen, setImportOpen] = useState<null | { onboarding: boolean }>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented && !importOpen) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, importOpen]);

  async function act(fn: () => Promise<unknown>, ok?: string) {
    setErr("");
    setBusy(true);
    try {
      await fn();
      if (ok) toast.success(ok);
      return true;
    } catch (e) {
      setErr(e instanceof Error ? e.message : "처리하지 못했습니다.");
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function addCalendar() {
    const ok = await act(() => cal.add(newName, newColor), "캘린더를 추가했습니다.");
    if (ok) { setAdding(false); setNewName(""); setNewColor(nextCalendarColor([...cal.own, { id: "x", name: "", color: newColor, order: 0 }])); }
  }

  async function saveRename() {
    if (!editing) return;
    const ok = await act(() => cal.rename(editing.id, editing.name), "이름을 바꿨습니다.");
    if (ok) setEditing(null);
  }

  async function removeCalendar(c: UserCalendar) {
    const yes = await confirm({
      title: `'${c.name}' 캘린더를 삭제할까요?`,
      summary: "이 캘린더의 일정은 지워지지 않고 기본 캘린더(내 일정)로 옮겨집니다.",
      confirmLabel: "삭제",
      kind: "danger",
    });
    if (!yes) return;
    await act(async () => {
      const moved = await cal.remove(c.id);
      toast.success(moved > 0 ? `캘린더를 삭제하고 일정 ${moved}건을 내 일정으로 옮겼습니다.` : "캘린더를 삭제했습니다.");
    });
  }

  async function connectGoogle() {
    const ok = await act(g.connect, "Google 캘린더를 연결했습니다.");
    if (ok) setImportOpen({ onboarding: true });
  }

  async function disconnectGoogle() {
    const yes = await confirm({
      title: "Google 캘린더 연결을 해제할까요?",
      summary: `${g.state?.email ?? ""} — ERP 화면에서만 Google 일정이 사라집니다. Google 캘린더의 일정은 그대로입니다.`,
      confirmLabel: "연결 해제",
      kind: "danger",
    });
    if (yes) await act(g.disconnect, "Google 캘린더 연결을 해제했습니다.");
  }

  async function removeImported(id: string) {
    const name = g.calendarOf(id)?.name ?? "";
    const yes = await confirm({
      title: `'${name}' 캘린더를 ERP 에서 뺄까요?`,
      summary: "ERP 달력에서만 사라집니다. Google 캘린더와 일정은 그대로입니다. 설정에서 다시 가져올 수 있습니다.",
      confirmLabel: "빼기",
      kind: "danger",
    });
    if (!yes) return;
    await act(() => g.setImported(g.imported.filter((c) => c.id !== id).map((c) => c.id)), "캘린더를 뺐습니다.");
  }

  const gBusy = g.busy !== null || busy;
  const canAdd = cal.own.length < USER_CALENDARS_MAX;
  const unavailable = cal.mode === "unavailable";

  return (
    <>
      <div className="modal-backdrop" {...dismiss}>
        <div className="modal-panel my-ev-modal my-cset-modal" role="dialog" aria-modal="true" aria-labelledby="my-cset-title">
          <div className="modal-head">
            <h3 id="my-cset-title">캘린더 설정</h3>
            <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
              <Icon name="close" size={16} />
            </button>
          </div>

          <div className="my-ev-body">
            {/* ── 내 캘린더 ── */}
            <section className="my-cset-sec">
              <div className="my-cset-hd">
                <span className="my-cset-title">내 캘린더</span>
                <span className="my-cset-sub">{cal.own.length}/{USER_CALENDARS_MAX}</span>
                {!adding && canAdd && !unavailable && (
                  <button type="button" className="btn-tiny" onClick={() => { setAdding(true); setErr(""); }} disabled={busy}>
                    <Icon name="plus" size={14} />
                    캘린더 추가
                  </button>
                )}
              </div>
              {unavailable && (
                <div className="my-local-note">
                  <Icon name="info" size={14} />
                  내 캘린더 추가는 준비 중입니다(서버 설정 배포 전). 지금은 기본 캘린더만 씁니다.
                </div>
              )}
              <ul className="my-list">
                {cal.calendars.map((c) => {
                  const isEdit = editing?.id === c.id;
                  return (
                    <li key={c.id} className="my-row my-cset-row">
                      <button
                        type="button"
                        className="my-cset-dotbtn"
                        style={{ background: calendarColorCss(c.color) }}
                        aria-label={`${c.name} 색 바꾸기`}
                        onClick={() => setColorFor(colorFor === c.id ? null : c.id)}
                        disabled={!!c.builtin || busy}
                      />
                      {isEdit ? (
                        <form
                          className="my-cset-inline"
                          onSubmit={(e) => { e.preventDefault(); void saveRename(); }}
                        >
                          <input
                            className="my-ev-inp"
                            value={editing.name}
                            maxLength={CALENDAR_NAME_MAX}
                            onChange={(e) => setEditing({ id: c.id, name: e.target.value })}
                            autoFocus
                            disabled={busy}
                            aria-label="캘린더 이름"
                          />
                          <button type="submit" className="btn-tiny" disabled={busy}>저장</button>
                          <button type="button" className="btn-tiny" onClick={() => setEditing(null)} disabled={busy}>취소</button>
                        </form>
                      ) : (
                        <>
                          <span className="my-row-title">{c.name}</span>
                          {c.builtin ? (
                            <span className="my-tag">기본</span>
                          ) : (
                            <span className="my-cset-acts">
                              <button type="button" className="my-cset-ibtn" aria-label={`${c.name} 이름 바꾸기`} onClick={() => { setEditing({ id: c.id, name: c.name }); setColorFor(null); }} disabled={busy}>
                                <Icon name="pencil" size={14} />
                              </button>
                              <button type="button" className="my-cset-ibtn is-danger" aria-label={`${c.name} 삭제`} onClick={() => void removeCalendar(c)} disabled={busy}>
                                <Icon name="trash" size={14} />
                              </button>
                            </span>
                          )}
                        </>
                      )}
                      {colorFor === c.id && !c.builtin && (
                        <div className="my-cset-colorrow">
                          <ColorPalette
                            value={c.color}
                            disabled={busy}
                            onPick={(k) => { setColorFor(null); void act(() => cal.recolor(c.id, k)); }}
                          />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              {adding && (
                <form className="my-cset-add" onSubmit={(e) => { e.preventDefault(); void addCalendar(); }}>
                  <label className="my-ev-field">
                    <span>새 캘린더 이름</span>
                    <input
                      className="my-ev-inp"
                      value={newName}
                      maxLength={CALENDAR_NAME_MAX}
                      placeholder="예: 업무, 가족, 운동"
                      onChange={(e) => setNewName(e.target.value)}
                      autoFocus
                      disabled={busy}
                    />
                  </label>
                  <div className="my-ev-field">
                    <span>색</span>
                    <ColorPalette value={newColor} onPick={setNewColor} disabled={busy} />
                  </div>
                  <div className="my-cset-addacts">
                    <button type="button" className="btn-ghost" onClick={() => { setAdding(false); setNewName(""); }} disabled={busy}>취소</button>
                    <button type="submit" className="btn-primary btn-sm" disabled={busy || !newName.trim()}>{busy ? "추가 중…" : "추가"}</button>
                  </div>
                </form>
              )}
            </section>

            {/* ── 연동 캘린더 ── */}
            <section className="my-cset-sec">
              <div className="my-cset-hd">
                <span className="my-cset-title">연동 캘린더</span>
              </div>

              {/* Google */}
              <div className="my-cset-svc">
                <div className="my-cset-svchd">
                  <span className="my-tbadge sp-google">Google</span>
                  <span className="my-cset-svcsub">
                    {g.mode === "unavailable" && "준비 중(서버 설정 배포 전)"}
                    {g.mode === "error" && "상태를 불러오지 못했습니다"}
                    {g.mode === "unlinked" && "아이폰·Google 일정을 함께 보고 고칩니다"}
                    {(g.mode === "linked" || g.mode === "needs_relink") && g.state?.email}
                    {g.mode === "linked" && ` · ${g.busy === "sync" ? "동기화 중…" : syncLabel(g.state?.last_sync_at)}`}
                  </span>
                  {g.mode === "unlinked" && (
                    <button type="button" className="btn-primary btn-sm" onClick={() => void connectGoogle()} disabled={gBusy}>
                      <Icon name="calendar" size={14} />
                      {g.busy === "link" ? "연결 중…" : "Google 연결"}
                    </button>
                  )}
                  {g.mode === "needs_relink" && (
                    <button type="button" className="btn-primary btn-sm" onClick={() => void connectGoogle()} disabled={gBusy}>
                      {g.busy === "link" ? "연결 중…" : "다시 연결"}
                    </button>
                  )}
                </div>
                {g.mode === "needs_relink" && (
                  <div className="my-local-note is-error">
                    <Icon name="warning" size={14} />
                    Google 연결이 끊겼습니다{g.state?.last_error ? ` — ${g.state.last_error}` : ""}. 다시 연결하세요.
                  </div>
                )}
                {g.mode === "linked" && (
                  <>
                    {g.state?.last_error && (
                      <div className="my-local-note is-error">
                        <Icon name="warning" size={14} />
                        마지막 동기화 실패 — {g.state.last_error}
                      </div>
                    )}
                    {g.needsPick && (
                      <div className="my-local-note">
                        <Icon name="info" size={14} />
                        가져올 캘린더를 아직 고르지 않아 전부 보입니다. [가져오기 변경]에서 고르세요.
                      </div>
                    )}
                    <ul className="my-list">
                      {g.imported.map((c) => (
                        <li key={c.id} className="my-row my-cset-row">
                          <i className="my-gcal-dot" style={{ background: c.color || undefined }} aria-hidden="true" />
                          <span className="my-row-title">{c.name}</span>
                          {c.access_role !== "owner" && c.access_role !== "writer" && <span className="my-tag">읽기 전용</span>}
                          <span className="my-cset-acts">
                            <button type="button" className="my-cset-ibtn is-danger" aria-label={`${c.name} 빼기`} onClick={() => void removeImported(c.id)} disabled={gBusy}>
                              <Icon name="trash" size={14} />
                            </button>
                          </span>
                        </li>
                      ))}
                    </ul>
                    {g.imported.length === 0 && <div className="my-block-empty">가져온 캘린더가 없습니다</div>}
                    <div className="my-cset-svcacts">
                      <button type="button" className="btn-tiny" onClick={() => setImportOpen({ onboarding: false })} disabled={gBusy}>
                        <Icon name="plus" size={14} />
                        가져오기 변경
                      </button>
                      <button type="button" className="btn-tiny" onClick={() => void act(g.syncNow)} disabled={gBusy}>
                        <Icon name="refresh" size={14} />
                        {g.busy === "sync" ? "동기화 중…" : "동기화"}
                      </button>
                      <span className="my-ev-spacer" />
                      <button type="button" className="btn-tiny" onClick={() => void disconnectGoogle()} disabled={gBusy}>연결 해제</button>
                    </div>
                  </>
                )}
              </div>

              {/* 네이버 — 준비 중 */}
              <div className="my-cset-svc is-soon" aria-disabled="true">
                <div className="my-cset-svchd">
                  <span className="my-tbadge sp-naver">네이버</span>
                  <span className="my-cset-svcsub">준비 중 — 연동 여부 검토 중입니다</span>
                </div>
              </div>
            </section>

            {err && <div className="my-ev-err" role="alert">{err}</div>}
          </div>

          <div className="my-ev-footer">
            <span className="my-ev-spacer" />
            <button type="button" className="btn-ghost" onClick={onClose}>닫기</button>
          </div>
        </div>
      </div>

      {importOpen && g.state && (
        <GoogleImportModal
          email={g.state.email}
          calendars={g.calendars}
          importedIds={g.state.imported}
          onboarding={importOpen.onboarding}
          onSave={async (ids) => {
            await g.setImported(ids);
            toast.success(ids.length ? `Google 캘린더 ${ids.length}개를 가져왔습니다.` : "가져올 캘린더를 비웠습니다.");
          }}
          onClose={() => setImportOpen(null)}
        />
      )}
    </>
  );
}
